import React, { lazy, useState, useEffect, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useContextHook } from 'use-context-hook';
import pages from '../nav.json';
import { AuthContext } from '../Context/authContext';
import Toast from '../components/Toast';
import Loaders from '../components/Loaders';
import Layout from '../components/Layout';

function PageLoadError({ file }) {
  return (
    <div className="p-8 max-w-lg">
      <h1 className="text-2xl font-bold text-ink mb-2">Couldn’t load this page</h1>
      <p className="text-mute text-sm mb-4">
        The “{file}” view failed to download (often a stale cache or incomplete deploy). Refresh to try
        again.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90">
        Refresh
      </button>
    </div>
  );
}

/** Cache lazy views so we don’t recreate React.lazy() types on every navigation. */
const viewCache = {};

const importView = file => {
  if (!file) {
    return () => <PageLoadError file="unknown" />;
  }
  if (!viewCache[file]) {
    viewCache[file] = lazy(() =>
      import(`./${file}`).catch(err => {
        console.error(`Failed to load page "./${file}"`, err);
        Toast({
          type: 'error',
          message: `Error in importing ${file}`,
        });
        // React.lazy requires a module with `default`. Returning nothing caused:
        // TypeError: Cannot read properties of undefined (reading 'default')
        return { default: () => <PageLoadError file={file} /> };
      }),
    );
  }
  return viewCache[file];
};

export default function Index() {
  const { loading_user, user } = useContextHook(AuthContext, ['loading_user', 'user']);

  function isEmpty(obj) {
    return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
  }
  const history = useNavigate();

  const { view } = useParams();
  const metaViewData = pages;
  const [selectedView, setSelectedView] = useState([]);

  function loadView(filtered) {
    const views = filtered.filter(Boolean).map(_view => {
      const View = importView(_view?.file);
      // eslint-disable-next-line no-use-before-define
      return <View key={_view?.id} selectView={selectView} />;
    });
    setSelectedView(views);
  }

  function selectView(file) {
    const filtered = metaViewData.filter(elem => elem.file === file);
    loadView([filtered[0]]);
  }

  useEffect(() => {
    let fileToLoad = view;

    history(`/${fileToLoad}`, { replace: true });

    selectView(fileToLoad);
  }, [view, loading_user]);

  return loading_user || isEmpty(user) ? (
    <Loaders viewLoader />
  ) : (
    <Layout showTemplate={metaViewData?.filter(elem => elem.file === view)[0]?.navigations} user={user}>
      <Suspense fallback={<Loaders viewLoader />}>{selectedView}</Suspense>
    </Layout>
  );
}
